import type { CityConfig } from "../city-template/types";

export const renoConfig: CityConfig = {
  city: "Reno",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "reno",
  route: "/cities/reno",
  title: "Emergency Passport Reno, NV — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport renewal in Reno, NV — as fast as 24 hours. Fast passport services for Washoe County, Northern Nevada, and Truckee Meadows travelers. A+ BBB rated, registered State Dept. courier. No agency visit required.",
  h1: "Emergency & Expedited Passport Services in Reno, NV",
  heroSubtitle: "Passport expired before your trip? Need emergency passport renewal in Reno? Fast Passport Center helps Washoe County travelers get expedited passports in as little as 24 hours. No agency visit required. Nevada has no local passport agency — we're your fastest option.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Reno%20Nevada%20downtown%20skyline%20at%20golden%20hour%20with%20Virginia%20Street%20arch%20and%20Truckee%20River%2C%20Sierra%20Nevada%20mountain%20peaks%20with%20snow%20visible%20in%20the%20distance%2C%20warm%20amber%20evening%20light%20over%20the%20Biggest%20Little%20City%20downtown%20district%2C%20colorful%20entertainment%20district%20buildings%20and%20casino%20hotels%2C%20wide%20panoramic%20view%20with%20clear%20Nevada%20sky%2C%20professional%20cityscape%20photography%20with%20mountain%20backdrop%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=reno-hero-001&orientation=landscape",
  heroImageAlt: "Reno Nevada downtown skyline at golden hour with Truckee River and Sierra Nevada mountains",
  geoPosition: "39.5296;-119.8138",
  geoPlacename: "Reno, Nevada",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco CA 94105",
    distance: "Approximately 220 miles — about 3.5 hours via I-80. The nearest passport agency to Reno and Northern Nevada.",
  },
  nearbyCitiesZips: [
    { name: "Downtown Reno", zips: "89501, 89502, 89503" },
    { name: "Sparks", zips: "89431, 89434, 89436" },
    { name: "Sun Valley", zips: "89433" },
    { name: "Verdi", zips: "89439" },
    { name: "Fernley", zips: "89408" },
    { name: "Carson City", zips: "89701, 89703, 89705" },
    { name: "Dayton", zips: "89403" },
    { name: "Fallon", zips: "89406" },
    { name: "Minden / Gardnerville", zips: "89423, 89410" },
    { name: "Truckee (CA)", zips: "96161, 96162" },
    { name: "South Lake Tahoe (CA)", zips: "96150, 96151" },
    { name: "Incline Village", zips: "89451" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Reno and Northern Nevada departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — ship from any UPS in Reno or Sparks" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Sparks", route: "/cities/sparks" },
    { name: "Fast passport renewal in Las Vegas", route: "/cities/las-vegas" },
    { name: "Expedited passport help in Henderson NV", route: "/cities/henderson-nv" },
    { name: "Urgent passport services in Sacramento", route: "/cities/sacramento" },
    { name: "Same-day passport help in San Francisco", route: "/cities/san-francisco" },
    { name: "Passport courier services in Carson City area", route: "/states/nevada" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Reno from our local office at 100 W Liberty St. Visit us by appointment, or use our courier pickup throughout Washoe County and Northern Nevada.",
  fpcOfficeDistance: "100 W Liberty St — Downtown Reno",
  fpcOfficeAddress: "100 W Liberty St, Reno, NV 89501",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the entire Reno-Sparks metro area and Northern Nevada. From downtown Reno to Sparks, Carson City, Fernley, and the Lake Tahoe basin — we help Northern Nevada travelers get their passports fast without the 220-mile drive to San Francisco.",
  nearbyCitiesIntro: "We serve all Reno neighborhoods and nearby Northern Nevada and Lake Tahoe area cities including Sparks, Carson City, Fernley, Dayton, Fallon, Minden, Gardnerville, Incline Village, Truckee (CA), and South Lake Tahoe (CA). If you are in Washoe County or the Northern Nevada corridor, we have you covered.",
  howItWorksIntro: "Most Reno travelers complete everything online and ship documents overnight. No need to drive to San Francisco or Sacramento. Our emergency passport process delivers fast results for Northern Nevada travelers.",
  mistakesIntro: "Reno travelers often don't realize how far the nearest passport agency is. Here are the most common passport mistakes we see in Northern Nevada.",
  testimonialsIntro: "Reno and Washoe County travelers trust us when their international departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Reno? Here are the most common ones we hear from Washoe County and Northern Nevada travelers.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Nearest agency is San Francisco (220 miles via I-80)",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Reno travelers with immediate departure needs — as fast as 24 hours for urgent flights from Reno-Tahoe International Airport.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support across Washoe County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance — especially helpful for travelers who lose passports at Tahoe resorts or Reno casinos.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Reno Nevada travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens in Reno NV and Washoe County with a passport issued within the last 15 years." },
    { name: "New Passport", description: "First-time passport application assistance with guidance through Washoe County acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Nicole B.",
      location: "Reno, NV",
      rating: 5,
      text: "International tech conference in Tokyo and my passport expired 2 months before the trip. Fast Passport Center renewed it in 36 hours. Saved my whole career trip.",
      service: "Emergency Passport",
    },
    {
      name: "Tom R.",
      location: "Sparks, NV",
      rating: 5,
      text: "Family vacation to Mexico and my wife's passport was expired. 4 days before the flight they got us a renewal. The kids finally got their beach trip.",
      service: "Passport Renewal",
    },
    {
      name: "Amber L.",
      location: "Carson City, NV",
      rating: 5,
      text: "Lost my passport at a Lake Tahoe ski resort before my Europe trip. They replaced it in 2 days. Incredible service — I made my flight and had the best ski trip of my life.",
      service: "Lost Passport",
    },
    {
      name: "Marcus W.",
      location: "Incline Village, NV",
      rating: 5,
      text: "Tech professional traveling to Europe frequently. Fast Passport Center has been reliable every time — fast processing, great communication, no surprises.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "reno-1",
      name: "Reno Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 S Virginia St",
      city: "Reno",
      state: "NV",
      zip: "89501",
      phone: "(775) 788-0900",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.1 miles from downtown Reno",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary downtown Reno facility. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "reno-2",
      name: "Sparks Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1075 Baring Blvd",
      city: "Sparks",
      state: "NV",
      zip: "89434",
      phone: "(775) 355-4405",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "In central Sparks",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Sparks and eastern Washoe County. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "reno-3",
      name: "Carson City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1385 Musser St",
      city: "Carson City",
      state: "NV",
      zip: "89701",
      phone: "(775) 882-2155",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles south of Reno",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Carson City and Douglas County. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "reno-4",
      name: "Washoe County Government Complex",
      type: "County Clerk Passport Acceptance",
      address: "1001 E 9th St",
      city: "Reno",
      state: "NV",
      zip: "89512",
      phone: "(775) 328-3275",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "1 mile from downtown Reno",
      website: "https://www.washoecounty.gov/",
      websiteLabel: "Washoe County Clerk",
      notes: "Full-service county facility. Passport acceptance with appointment. Extended hours available.",
      icon: "ri-government-line",
    },
    {
      id: "reno-5",
      name: "University of Nevada Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1664 N Virginia St",
      city: "Reno",
      state: "NV",
      zip: "89557",
      phone: "(775) 784-6490",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "On the UNR campus",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for UNR students, faculty, and North Reno residents. Call ahead for availability.",
      icon: "ri-mail-line",
    },
    {
      id: "reno-6",
      name: "Fernley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "440 E Main St",
      city: "Fernley",
      state: "NV",
      zip: "89408",
      phone: "(775) 575-2421",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles east of Reno",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Lyon County and the Fernley-Fallon corridor. Limited hours — call ahead.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — call NPIC, limited slots. No walk-ins accepted.", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — 220-mile drive to San Francisco Passport Agency via I-80", fpc: "Ship documents overnight from Reno — no drive needed" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility with proof of travel", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public — agency serves emergencies only", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Reno-Specific Guidance", agency: "Generic — no RNO airport, Tahoe, or Northern Nevada expertise", fpc: "Full Reno/Tahoe/Northern Nevada knowledge built in" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Reno photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Reno acceptance facilities.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Forgetting to Account for Sierra Nevada Travel Time",
      description: "The San Francisco Passport Agency is 220 miles from Reno — but Donner Pass can add hours in winter. Using our courier service eliminates this risk entirely.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Reno and Sparks families at the acceptance clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Nevada has no local passport agency. Standard processing takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Reno or Sparks." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Reno address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Reno?",
      a: "Fast Passport Center offers emergency passport processing in Reno in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. Nevada has no local passport agency — the nearest one is in San Francisco, 220 miles away — so our courier service is the fastest, most practical option for Reno travelers.",
    },
    {
      q: "What is the nearest passport agency to Reno?",
      a: "The nearest passport agency to Reno is the San Francisco Passport Agency, approximately 220 miles via I-80. There is also the Los Angeles Passport Agency (about 440 miles south). Most Reno travelers find it far more practical to use Fast Passport Center's 24–48 hour courier service rather than making the long drive to San Francisco — especially in winter when Donner Pass can add hours.",
    },
    {
      q: "Where can I get a fast passport renewal in Reno?",
      a: "Fast Passport Center offers fast passport renewal in Reno with no office visit required for most adult renewals. We serve downtown Reno, Sparks, Sun Valley, Carson City, Fernley, Incline Village, and the entire Truckee Meadows area. Complete your application online, ship your documents using our prepaid overnight label, and our couriers handle everything else.",
    },
    {
      q: "Can Reno travelers use the San Francisco Passport Agency for emergencies?",
      a: "Yes, but only for documented life-or-death emergencies with travel within 72 hours. The San Francisco Passport Agency is 220 miles from Reno, and Donner Pass can make the drive even longer in winter. For all other urgent travel, Fast Passport Center's 24–48 hour courier processing is the faster and safer option.",
    },
    {
      q: "What if I'm traveling internationally from Reno-Tahoe International Airport?",
      a: "Reno-Tahoe International Airport (RNO) offers direct service to Mexico and connects to international hubs. If your passport is expired or about to expire, Fast Passport Center can process your renewal or emergency passport in 24–48 hours — well before your departure from RNO.",
    },
    {
      q: "Do you serve Lake Tahoe and Incline Village?",
      a: "Yes. Fast Passport Center serves the entire Lake Tahoe basin including Incline Village, Crystal Bay, and the North Shore communities. We also serve South Lake Tahoe (CA) and Truckee (CA). Our overnight shipping labels work from any UPS location, so your remote mountain location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Reno?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour, 3–5 day expedited, or standard options. Contact us for a quote based on your departure date.",
    },
    {
      q: "What if I need a passport for a UNR study abroad program?",
      a: "Fast Passport Center helps University of Nevada Reno students get passports for study abroad programs. Whether you need a first-time passport or an expedited renewal, we guide you through the process and ensure your documents are ready before your program departure date.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Downtown Reno", address: "675 W 2nd St, Reno, NV 89503", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Sparks", address: "1075 Baring Blvd, Sparks, NV 89434", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Reno", address: "1201 Terminal Way, Reno, NV 89502", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — South Reno", address: "4855 Kietzke Ln, Reno, NV 89509", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Sparks", address: "2355 Market St, Sparks, NV 89434", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Carson City", address: "1815 US-395, Carson City, NV 89701", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Reno South", address: "4905 S Virginia St, Reno, NV 89502", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Costco — Reno", address: "333 Bill Ave, Reno, NV 89502", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Nevada DMV — Reno",
      address: "555 Wright Way, Carson City, NV 89711",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Primary Northern Nevada DMV. REAL ID processing. Online appointment recommended.",
      featured: true,
    },
    {
      name: "Nevada DMV — Sparks",
      address: "1775 Hymer Ave, Sparks, NV 89431",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Sparks branch for Washoe County residents. Full-service DMV with REAL ID.",
      featured: false,
    },
    {
      name: "Nevada DMV — Carson City",
      address: "555 Wright Way, Carson City, NV 89711",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "State capital DMV with full services. Alternative for south Reno and Carson Valley residents.",
      featured: true,
    },
    {
      name: "Washoe County Clerk",
      address: "1001 E 9th St, Reno, NV 89512",
      hours: "Mon–Fri: 8AM–5PM",
      note: "County government center with passport acceptance. Appointments recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "RNO",
      name: "Reno-Tahoe International Airport",
      distance: "4 miles from downtown Reno",
      address: "2001 E Plumb Ln, Reno, NV 89502",
      note: "The primary airport for Reno and Northern Nevada. Direct service to Mexico and connecting international flights. Major hub for Tahoe ski season travel and Tesla/tech industry corporate travel.",
      flightUrl: "https://www.renoairport.com/",
      flightLabel: "Reno-Tahoe Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "220 miles from Reno via I-80",
      address: "San Francisco, CA 94128",
      note: "Primary West Coast international hub. Home to the San Francisco Passport Agency for emergency appointments. Often used by Reno residents for extended international routing.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport",
      featured: false,
    },
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "130 miles from Reno via I-80",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Alternative regional hub with international flights. Closer than San Francisco for some Reno itineraries.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
  ],
};